import { promises as fs } from 'fs';
import { AppFragment } from 'enums/path-fragment';
import { isServerSide } from 'utility/constant';
import { isNotEmpty } from 'utility/functions';
import { seoItemHierarchy, seoItemType } from 'components/staticMap/types';

const fromWeb = async (): Promise<Array<seoItemType>> => {
  const result = await fetch(
    `${process.env.NEXT_PUBLIC_BASE_URL}/api/static?rsx=${encodeURIComponent('public/assets/pages.json')}`
  );
  const data = await result.json();
  return data as Array<seoItemType>;
};
const fromFile = async (): Promise<Array<seoItemType>> => {
  const content = await fs.readFile('./public/assets/pages.json', { encoding: 'utf-8' });
  return JSON.parse(content) as Array<seoItemType>;
};

const doLoad = isServerSide ? fromFile : fromWeb;
let TREE: Array<seoItemType> = [];

export const loadIndex = async (): Promise<Array<seoItemType>> => {
  TREE = await doLoad();
  return TREE;
};

const fallback = (pathName: string): seoItemType => {
  const title = pathName.split('/').pop() ?? pathName;

  const cId = new Date().toISOString().substring(0, 10).replace(/-/gim, '');
  const rnd = `${Math.random()}`.substring(2);

  return { title, uid: `${cId}-${rnd}` };
};

export const slugAvvenimentoForCMS = (slug: string): string => {
  let loc = `/${decodeURIComponent(slug ?? '')}`.toLowerCase();
  const index = TREE.findIndex((x) => `${x.loc}`.toLowerCase() === loc);
  if (index < 0) {
    const path = loc.split('/');
    const last = path.pop();
    const comp = last?.split(' - ');
    if (comp?.length === 2) {
      const rev = comp.reverse().join(' - ');
      path.push(rev);
      loc = path.join('/');
    }
  }
  const result = loc.substring(1);
  // console.log(`slugAvvenimentoForCMS("${slug}") -> "${result}"`)
  return result;
};

export const loadFlat = (pathName: string, depth = Infinity, includeSelf = false): Array<seoItemType> => {
  const tmp = decodeURIComponent(pathName ?? '')
    .toLowerCase()
    .split('');
  if (tmp[0] === '/') tmp.shift();
  if (tmp[tmp.length - 1] === '/') tmp.pop();

  const computed = ['/', ...tmp].join('');

  const result = TREE.filter((x) => `${x.loc}`.startsWith(`${computed}/`)).filter(
    (x) => depth === Infinity || `${x.loc}`.split('/').length === depth + 1
  );

  if (includeSelf) {
    const self = TREE.find((x) => `${x.loc}` === computed) ?? fallback(computed);
    result.splice(0, 0, self);
  }

  return result.filter((el, idx, lst) => isNotEmpty(el.loc) && lst.findIndex((k) => k.loc === el.loc) === idx);
};

export const loadTree = (pathName: string, depth = Infinity): seoItemHierarchy => {
  const root = `${pathName ?? ''}`.replace(/(\/*)$/, '');
  const wTree = TREE.filter((x) => (`${x.loc}`.startsWith(`/${AppFragment.Sport}/info`) ? false : true));

  const lng = decodeURIComponent(root ?? '').split('/').length;

  let self = wTree.find((x) => `${x.loc}`.toLowerCase() === `${root}`.toLowerCase()) ?? fallback(root);

  const children = wTree
    .filter((x) => {
      if (`${x.loc}`.toLowerCase().indexOf(`${root}/`.toLowerCase()) === 0) {
        return `${x.loc}`.replace(/(\/*)$/, '').split('/').length - lng <= depth;
      }
      return false;
    })
    .map((x) => {
      // transform to root/computed-name (1 level child)
      const segments = `${x.loc}`.split('/');
      return segments.splice(0, lng + 1).join('/');
    })
    .filter(
      (el, idx, lst) =>
        // unique
        lst.indexOf(el) === idx
    )
    .sort()
    .map((loc) => {
      if (lng < depth) {
        return loadTree(loc, depth);
      } else {
        return { self: { ...fallback(loc), loc } } as seoItemHierarchy;
      }
    });

  return {
    self,
    children,
  };
};